import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "react-query";
import { api } from "../api";
import { UserSetting, UserSettingName } from "../../types/UserSetting";
import { useMemo } from "react";

enum QueryKeys {
  USER_SETTING = "user-setting",
}

export const useGetUserSettings = (options?: UseQueryOptions<UserSetting[]>) =>
  useQuery<UserSetting[]>([QueryKeys.USER_SETTING], () => api.get("v2/usersetting", {}), {
    ...options,
    staleTime: Infinity,
  });

type UserSettingValueType = string | number | boolean;
export function useGetUserSetting<T extends UserSettingValueType = string>(
  settingName: UserSettingName,
  expectedType: "string" | "number" | "boolean" = "string"
): { isLoading: boolean; value: T | undefined } {

  const { data: userSettings, isLoading } = useGetUserSettings();

  const rawValue = useMemo(() => {
    return (userSettings ?? []).find(x => x.name === settingName)?.value;
  }, [userSettings, settingName]);

  const parsedValue = useMemo(() => {
    if (rawValue == null) return undefined;

    switch (expectedType) {
      case 'boolean':
        return (rawValue === 'true' || rawValue === '1') as T;
      case 'number': {
        const num = Number(rawValue);
        return (isNaN(num) ? undefined : num) as T;
      }
      case 'string':
      default:
        return rawValue as T;
    }
  }, [rawValue, expectedType]);

  return { isLoading, value: parsedValue };
}

interface UpdateSettingInput {
  userId: string;
  name: string;
  value: string;
}

export const useUpdateUserSettingMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  const handleUpdateUserSetting = (input: UpdateSettingInput) => {
    return api.put(`v2/usersetting/${input.name}`, input);
  };

  return useMutation(handleUpdateUserSetting, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.USER_SETTING);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};
